import React, { useState, useEffect } from 'react'
import { Box, Typography, CircularProgress, Icon } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import LearnMoreModal from '../components/LearnMoreModal'
import RequestAttachmentUploader from '../components/RequestAttachmentUploader'
import axios from 'lib/axios'

import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'
import { downloadResponseFile } from 'services/helper/fileDownload'
import { getHrAccessToken } from 'services/session/authSession'

const LeaveImportForm = ({ formikProps, errorResponse }) => {
  const { values, setFieldValue, handleSubmit, errors } = formikProps
  const [isLoading, setIsLoading] = useState(false)
  const [downloadError, setDownloadError] = useState()

  useEffect(() => {
    if (values.file) {
      handleSubmit()
    }
  }, [values.file]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async (filename) => {
    try {
      setIsLoading(true)
      const hr_access_token = getHrAccessToken()

      const res = await axios.get(`admin/entitlement/export?mode=template_all_staff`, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${hr_access_token}`,
        },
      })

      if (res.data) {
        downloadResponseFile(res, {
          fileName: filename,
          contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          successMessage: 'Leave balance template downloaded.',
        })
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      setDownloadError(
        error?.response?.data?.message || 'Please complete all the leave policy setup first!!'
      )
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 2,
      }}
    >
      <Box sx={{ width: 1 }}>
        <Typography align="left" variant="body2" color="light-gray-text.main">
          Upload Excel file to import staff leave allocation and balance. {'('}
          Disclaimer: This will overwrite existing employee information. Errors will not be
          uploaded.{')'}
        </Typography>
      </Box>
      <Box sx={{ width: 1 }}>
        <Typography variant="body2" color="dark-gray-text.main">
          Step 1
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Typography variant="body2" color="light-gray-text.main">
            Download the Excel template and fill in the necessary details.
          </Typography>
          <LearnMoreModal />
        </Box>
      </Box>
      <Box sx={{ width: 1 }}>
        <AdxButton
          onClick={() => handleDownload('Adnexio-leave-bulk.xlsx')}
          isLoading={isLoading}
          sx={{ width: 1, display: 'flex', gap: 1 }}
        >
          <Icon>download</Icon>
          Download Leave Balance Template
        </AdxButton>
        <AdxErrorMessage>{downloadError}</AdxErrorMessage>
      </Box>
      <Box sx={{ width: 1 }}>
        <Typography variant="body2" color="dark-gray-text.main">
          Step 2
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography mr={1} variant="body2" color="light-gray-text.main">
            Upload completed Excel file
          </Typography>
          <Field name="file" values={values.file} type="hidden" />
          <RequestAttachmentUploader
            values={values}
            setFieldValue={setFieldValue}
            errorMessage={<ErrorMessage name="file" />}
            errorResponse={errorResponse}
            errors={errors}
          />
        </Box>
      </Box>

      {values?.file && !errors?.file && !errorResponse ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 2,
            width: 1,
          }}
        >
          <Typography variant="body2" color="error.main">
            Your file is being uploaded, we will move to the next step once completed.
          </Typography>
          <CircularProgress
            sx={{
              color: 'alternate.main',
            }}
            size={20}
          />
        </Box>
      ) : null}
      {errors?.file && values?.file?.length !== 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 2,
            width: 1,
          }}
        >
          <Typography variant="body2" color="error.main">
            {errors?.file}
          </Typography>
        </Box>
      ) : null}
      {errorResponse ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 2,
            width: 1,
          }}
        >
          <Typography variant="body2" color="error.main">
            {errorResponse}
          </Typography>
        </Box>
      ) : null}
    </Box>
  )
}

export default LeaveImportForm
